<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { atIndexMaybe, isJust, nothing, type Maybe } from "../../lib/maybe";
import ZwMenu from "./ZwMenu.vue";
import ZwMenuItem from "./ZwMenuItem.vue";

export type ZwSearchableMenuOption = {
  value: string;
  label: string;
};

const props = withDefaults(
  defineProps<{
    options: ZwSearchableMenuOption[];
    align?: "start" | "end";
    disabled?: boolean;
    /** Callers pass `just(text)` or `nothing` explicitly. */
    menuLabel: Maybe<string>;
    searchPlaceholder?: string;
    emptyLabel?: string;
  }>(),
  {
    align: "start",
    disabled: false,
    searchPlaceholder: "Search…",
    emptyLabel: "No matches",
  },
);

const emit = defineEmits<{
  select: [value: string];
}>();

const open = defineModel<boolean>("open", { default: false });

const query = ref("");
const highlightIndex = ref(0);
const searchInput = useTemplateRef<HTMLInputElement>("searchInput");

/**
 * Substring match against the option label (case-insensitive). Empty query
 * shows every option.
 */
function matchesOption(
  option: ZwSearchableMenuOption,
  normalizedQuery: string,
): boolean {
  if (normalizedQuery === "") return true;
  return option.label.toLowerCase().includes(normalizedQuery);
}

const filteredOptions = computed(() => {
  const normalized = query.value.trim().toLowerCase();
  return props.options.filter((option) => matchesOption(option, normalized));
});

watch(open, async (isOpen) => {
  if (isOpen === false) return;
  query.value = "";
  highlightIndex.value = 0;
  await nextTick();
  searchInput.value?.focus();
});

watch(filteredOptions, (options) => {
  if (highlightIndex.value >= options.length) {
    highlightIndex.value = Math.max(0, options.length - 1);
  }
});

function selectValue(value: string): void {
  emit("select", value);
  open.value = false;
}

function moveHighlight(delta: number): void {
  const count = filteredOptions.value.length;
  if (count === 0) return;
  highlightIndex.value = (highlightIndex.value + delta + count) % count;
}

function onSearchKeydown(event: KeyboardEvent): void {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    moveHighlight(1);
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    moveHighlight(-1);
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const option = atIndexMaybe(filteredOptions.value, highlightIndex.value);
    if (isJust(option)) selectValue(option.value.value);
  }
}
</script>

<template>
  <ZwMenu
    v-model:open="open"
    class="zw-searchable-menu"
    :align="align"
    :disabled="disabled"
    :menu-label="menuLabel"
  >
    <template #trigger="slotProps">
      <slot name="trigger" v-bind="slotProps" />
    </template>

    <div class="zw-searchable-menu-panel">
      <div class="zw-searchable-menu-search" @mousedown.stop>
        <input
          ref="searchInput"
          v-model="query"
          type="search"
          class="zw-searchable-menu-input"
          :placeholder="searchPlaceholder"
          autocomplete="off"
          writingsuggestions="false"
          spellcheck="false"
          aria-autocomplete="list"
          @keydown="onSearchKeydown"
        />
      </div>

      <div class="zw-searchable-menu-list" role="listbox">
        <p v-if="filteredOptions.length === 0" class="zw-searchable-menu-empty">
          {{ emptyLabel }}
        </p>
        <div
          v-for="(option, index) in filteredOptions"
          :key="option.value"
          class="zw-searchable-menu-option"
          :class="{
            'zw-searchable-menu-option-active': index === highlightIndex,
          }"
          @mouseenter="highlightIndex = index"
        >
          <ZwMenuItem
            :label="option.label"
            :shortcut="nothing"
            :title="nothing"
            @select="selectValue(option.value)"
          />
        </div>
      </div>

      <div
        v-if="typeof $slots.footer === 'function'"
        class="zw-searchable-menu-footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </ZwMenu>
</template>

<style>
.zw-searchable-menu {
  width: 100%;
}

.zw-searchable-menu-panel {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: min(18rem, 70vw);
}

.zw-searchable-menu-search {
  position: sticky;
  top: -0.3rem;
  z-index: 1;
  margin: -0.05rem -0.05rem 0.2rem;
  padding: 0.25rem 0.15rem 0.4rem;
  background: var(--bg-raised);
  border-bottom: 1px solid var(--line);
}

.zw-searchable-menu-input {
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  font: inherit;
  font-size: 0.88rem;
  color: var(--text);
}

.zw-searchable-menu-input::placeholder {
  color: var(--text-muted);
}

.zw-searchable-menu-input:focus {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

.zw-searchable-menu-input::-webkit-search-cancel-button {
  display: none;
}

.zw-searchable-menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-height: 0;
}

.zw-searchable-menu-empty {
  margin: 0;
  padding: 0.55rem 0.45rem;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.zw-searchable-menu-option-active .zw-menu-item {
  background: var(--hover-fill);
  color: var(--text-strong);
}

.zw-searchable-menu-footer {
  margin-top: 0.15rem;
  padding-top: 0.15rem;
  border-top: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}
</style>
